/**
 * People tab (§2.4, screenshots/03): viewpoints on a topic (vote split, who spoke in favor / against or
 * raised concerns), exchanges between members, and everyone who spoke. Stances are from what people
 * said, labelled "spoke in favor / against", not votes (brief §8).
 */
import { List, ListRow, RowLead, RowMain, RowSub, RowTitle, RowTrail } from "../shared/List";
import { useMemo, useState } from "react";
import { ArrowRight, ChevronDown, ChevronRight, Play, Search, Users } from "lucide-react";
import { Dropdown } from "../shared/Dropdown";
import { useShallow } from "zustand/react/shallow";
import { meetingStore, useMeeting } from "../../stores/meetingStore";
import { previewPerson } from "../../people/PersonPreview";
import { plainClick } from "../../app/router";
import { onTablistKey } from "../../hooks/useTabs";
import { Face } from "../shared/Face";
import { NoResults } from "../shared/States";
import { VoteSplitChart } from "./VoteSplitChart";
import { clock } from "../../lib/format";
import { seekTo } from "./vimeoClock";
import type { MeetingPeople, Person, Stance } from "../../types";
import { personHref } from "../../app/viewer";
import { seatLabel, site } from "../../app/site";

const STANCE_LABEL: Record<Stance, string> = { support: "For", oppose: "Against", concern: "Concern" };
const RELATION_TONE: Record<MeetingPeople["exchanges"][number]["relation"], string> = { agree: "agree", disagree: "disagree", thanks: "plain", noted: "plain", question: "plain" };
const EXCHANGES_SHOWN = 4;

/** Members of the public: residents (r:) and unidentified public commenters (x:). Person ids: meeting-snapshot.ts. */
const isResident = (id: string) => /^[rx]:/.test(id);
const isAlderperson = (id: string) => /^a:/.test(id);
type Who = "all" | "alderpersons" | "residents";
const WHO: [Who, string][] = [["all", "All"], ["alderpersons", site.Members], ["residents", "Residents"]];
const shows = (who: Who, id: string) => who === "all" || (who === "residents" ? isResident(id) : isAlderperson(id));
const seedOf = (id: string, person: Person | undefined, sub?: string) => ({ id, name: person?.name ?? "Speaker", ward: person?.ward, photoUrl: person?.photoUrl, sub });

function Position({ p, person }: { p: MeetingPeople["topics"][number]["positions"][number]; person: Person | undefined }) {
  const sub = [person?.ward != null ? seatLabel(person.ward) : null, p.role].filter(Boolean).join(" · ");
  return (
    <div className="position">
      <button type="button" className="position-face" aria-label={`Preview ${person?.name ?? "Speaker"}`} onClick={() => previewPerson(seedOf(p.personId, person, sub))}>
        <Face name={person?.name ?? "Speaker"} photoUrl={person?.photoUrl} size={34} />
      </button>
      <div className="position-text">
        <div className="position-name-row">
          <span className="position-name-group">
            <button type="button" className="position-name" onClick={() => previewPerson(seedOf(p.personId, person, sub))}>{person?.name ?? "Speaker"}</button>
            <span className="position-ward">{sub}</span>
          </span>
          <button className="jump" onClick={() => seekTo(p.at)}><Play size={10} />{clock(p.at)}</button>
        </div>
        <p className="viewpoint">{p.viewpoint}</p>
      </div>
    </div>
  );
}

export function PeopleTab() {
  const { people, data, outcomes } = useMeeting(useShallow((s) => ({ people: s.people, data: s.report?.people, outcomes: s.outcomes })));
  const report = useMeeting((s) => s.report);
  const [topicId, setTopicId] = useState(data?.topics[0]?.id ?? "");
  const [query, setQuery] = useState("");
  const [allExchanges, setAllExchanges] = useState(false);
  const [who, setWho] = useState<Who>("all");
  const turns = useMeeting((s) => s.turns);
  const topic = data?.topics.find((t) => t.id === topicId) ?? data?.topics[0];
  const outcome = topic?.outcomeId ? outcomes.find((o) => o.id === topic.outcomeId) : undefined;

  const speakers = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Members of the public who spoke (the report lists alderpersons): one row each, their turns as moments.
    const counts = new Map<string, number>();
    for (const t of turns) if (isResident(t.personId) && people[t.personId]) counts.set(t.personId, (counts.get(t.personId) ?? 0) + 1);
    const pub = [...counts].map(([personId, moments]) => ({ personId, moments, stance: undefined as Stance | undefined }));
    return [...(data?.speakers ?? []), ...pub]
      .filter((s) => shows(who, s.personId))
      .map((s) => ({ ...s, person: people[s.personId] }))
      .filter((s) => !q || s.person?.name.toLowerCase().includes(q) || String(s.person?.ward ?? "") === q.replace(new RegExp(`^${site.seat}\\s*`), ""))
      .sort((a, b) => (a.person?.ward ?? 99) - (b.person?.ward ?? 99));
  }, [data, people, query, who, turns]);

  if (!data || !report) return null;
  const favor = topic?.positions.filter((p) => p.stance === "support" && shows(who, p.personId)) ?? [];
  const against = topic?.positions.filter((p) => p.stance !== "support" && shows(who, p.personId)) ?? [];
  const exchanges = allExchanges ? data.exchanges : data.exchanges.slice(0, EXCHANGES_SHOWN);

  return (
    <div className="people-tab tab-panel" role="tabpanel">
      <section className="viewpoints">
        <div className="people-head">
          <div className="section-head">
            <h2 className="section-title">People &amp; viewpoints</h2>
            <span className="chip chip--neutral chip--medium">{report.alderpersonCount} {site.members} · {report.publicCommenters} public</span>
          </div>
          <div className="filter-tabs" role="tablist" aria-label="Who" onKeyDown={onTablistKey}>
            {WHO.map(([id, label]) => (
              <button key={id} role="tab" aria-selected={who === id} tabIndex={who === id ? 0 : -1} className="filter-tab" onClick={() => setWho(id)}>{label}</button>
            ))}
          </div>
          {topic && (
            <Dropdown className="topic-select" label="Topic" align="end" value={topic.id} onChange={setTopicId}
              icon={<span className="topic-select-label">On</span>} chevron={<ChevronDown size={14} aria-hidden />}
              options={data.topics.map((t) => ({ value: t.id, label: t.label }))} />
          )}
        </div>

        {outcome && outcome.yes + outcome.no > 0 && (
          <div className="vote-split-block">
            <div className="vote-labels">
              <span className="vote-yes">{outcome.yes} yes</span>
              {topic?.rollCall && <span className="vote-roll">Roll call vote · {clock(topic.rollCall.start)} – {clock(topic.rollCall.end)}</span>}
              <span className="vote-no">{outcome.no} no</span>
            </div>
            <VoteSplitChart outcomeId={outcome.id} />
          </div>
        )}

        {topic && (
          <div className="stances">
            <div className="stance-col">
              <h3 className="stance-head"><i className="stance-dot" data-stance="support" />Spoke in favor</h3>
              {favor.map((p) => <Position key={p.personId + p.at} p={p} person={people[p.personId]} />)}
            </div>
            <div className="stance-col">
              <h3 className="stance-head"><i className="stance-dot" data-stance="oppose" />Spoke against or raised concerns</h3>
              {against.map((p) => <Position key={p.personId + p.at} p={p} person={people[p.personId]} />)}
            </div>
          </div>
        )}

        {data.exchanges.length > 0 && (
          <div className="exchanges">
            <h3 className="exchanges-head">Exchanges between members</h3>
            {exchanges.map((x) => (
              <div className="exchange" key={`${x.at}-${x.from}-${x.to}`}>
                <button className="exchange-time" onClick={() => seekTo(x.at)}>{clock(x.at)}</button>
                <div className="exchange-text">
                  <div className="exchange-pair">
                    <span>{people[x.from]?.name ?? x.from}</span>
                    <ArrowRight size={12} />
                    <span>{people[x.to]?.name ?? x.to}</span>
                    <span className="relation" data-tone={RELATION_TONE[x.relation]}>{x.relation}</span>
                  </div>
                  <p className="exchange-detail">{x.detail}</p>
                </div>
              </div>
            ))}
            {!allExchanges && (data.exchangeCount ?? data.exchanges.length) > EXCHANGES_SHOWN && (
              <button className="link-arrow more-exchanges" onClick={() => setAllExchanges(true)}>
                Show all {data.exchangeCount ?? data.exchanges.length} exchanges<ArrowRight size={14} />
              </button>
            )}
          </div>
        )}
      </section>

      <aside className="everyone">
        <div className="list-head">
          <h3>{who === "residents" ? "Residents who spoke" : who === "alderpersons" ? `${site.Members} who spoke` : "Who spoke"}</h3>
          <span className="list-sort">By {site.seat}</span>
        </div>
        {data.publicComment && who !== "alderpersons" && (
          <button className="public-comment" onClick={() => seekTo(data.publicComment!.at)}>
            <Users size={16} />
            <span className="public-text">
              <span className="public-title">{data.publicComment.count} public commenters</span>
              <span className="public-meta">{data.publicComment.summary} · from {clock(data.publicComment.at)}</span>
            </span>
          </button>
        )}
        <label className="people-search">
          <Search size={14} />
          <input placeholder={`Find a person or ${site.seat}`} aria-label={`Find a person or ${site.seat}`} value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
        <List className="people-list">
          {speakers.map((s) => {
            const meta = `${s.person?.ward != null ? `${seatLabel(s.person.ward)} · ` : ""}${s.moments} ${s.moments === 1 ? "moment" : "moments"}`;
            return (
              <ListRow key={s.personId} className="person-row" href={personHref(s.personId)}
                onOpen={() => previewPerson(seedOf(s.personId, s.person, meta))}>
                <RowLead><span className="face-wrap" data-stance={s.stance}><Face name={s.person?.name ?? "Speaker"} photoUrl={s.person?.photoUrl} size={28} /></span></RowLead>
                <RowMain min={120}>
                  <RowTitle className="person-name">{s.person?.name ?? "Speaker"}</RowTitle>
                  <RowSub className="person-meta">{meta}</RowSub>
                </RowMain>
                <RowTrail>
                  {s.stance && <span className="person-stance"><i className="stance-dot stance-dot--sm" data-stance={s.stance} />{STANCE_LABEL[s.stance]}</span>}
                  <ChevronRight size={14} className="person-chevron" />
                </RowTrail>
              </ListRow>
            );
          })}
        </List>
        {speakers.length === 0 && (query.trim() || who !== "all") && <NoResults query={query.trim() || WHO.find(([id]) => id === who)![1]} />}
      </aside>
    </div>
  );
}
